<template>
  <el-row class="wrapper flex-fill px-3 px-md-5 mt-5">
    <el-col :sm="{ span: 12, offset: 6 }" :md="{ span: 12, offset: 6 }" :lg="{ span: 8, offset: 8 }" :xl="{ span: 10, offset: 7 }">
      <div class="d-flex flex-column gap-3 justify-content-center h-100 form-wrapper">
        <div class="d-flex mb-4 align-items-center justify-content-center logo">
          <img id="logo" :src="userStore.domain?.logo ?? defaultLogoPathWhite" />
        </div>
        <div class="bg-white p-4">
          <h3 class="mb-4">{{ $t('components.login.loginToContinue') }}</h3>
          <el-form ref="dataForm" :model="loginState" label-position="top" require-asterisk-position="right" @submit="submitLogin">
            <el-form-item
              :label="$t('components.login.emailOrUsername')"
              prop="username"
              required
              class="form-text"
              :rules="{
                required: true,
                message: $t('error.vuelidate.required'),
              }"
            >
              <el-input v-model="loginState.username" type="text" class="custom-input"/>
            </el-form-item>
            <el-form-item
              :label="$t('components.login.password')"
              prop="password"
              required
              class="form-text"
              :rules="{
                required: true,
                message: $t('error.vuelidate.required'),
              }"
            >
              <el-input v-model="loginState.password" type="password" class="custom-input" autocomplete="off" />
            </el-form-item>
            <el-form-item>
              <div class="d-grid w-100 pt-2">
                <el-button class="button" type="primary" native-type="submit" :disabled="isLoading">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else>{{ $t('components.login.logIn') }}</span>
                </el-button>
              </div>
            </el-form-item>
          </el-form>
          <div class="d-flex footer flex-column align-items-center fw-normal">
            <el-text
              >{{ $t('components.login.needAccount') }}
              <a href="/register" class="text-primary text-decoration-none">{{ $t('components.login.signUp') }}</a></el-text
            >
            <el-text
              >{{ $t('components.login.forgotCredentials') }}
              <a href="/forgot-password" class="text-primary text-decoration-none">{{ $t('components.login.clickHere') }}</a></el-text
            >
          </div>
        </div>
        <div class="d-flex flex-column align-items-center gap-3 fw-normal mt-4">
          <el-text><a href="#" class="text-primary text-decoration-none" @click="handleGoBack">{{ $t('components.login.routeGoBack') }}</a></el-text>
        </div>
      </div>
    </el-col>
    <el-dialog v-model="dialogVisible" class="dialog-container" :title="justRegistered || showThankYou ? $t('components.login.registerSuccess') : $t('components.login.passwordUpdated')">
      <div>
        <div v-if="justRegistered" style="text-align: center">
          <p class="dialog">{{ $t('components.login.registerCheck') }}</p>
        </div>
        <div v-if="showThankYou" style="text-align: center">
          <p class="dialog">{{ $t('components.login.registerThankYou') }}</p>
        </div>
        <div v-if="justResetPassword" style="text-align: center">
          <p class="dialog">{{ $t('components.login.loginWithNewPassword') }}</p>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button class="button" @click="handleOK">{{ $t('components.login.ok') }}</el-button>
        </span>
      </template>
    </el-dialog>
  </el-row>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import { ElForm, ElMessage, ElMessageBox } from 'element-plus';
import { endpointAuthSystem } from '@/services/api/api';
import Cookies from 'js-cookie';
import { Key } from '@/types/constants/Key';
import { useUserStore } from '@/stores/userStore';
import { defaultLogoPathWhite } from '@/utils/theme';

import { useRoute, useRouter } from 'vue-router';
import { initializeSession } from '@/services/api/sessionService';

interface LoginForm {
  username: string;
  password: string;
}

const verifyKeys = ['check', 'success', 'update-success'];

@Component
export default class Login extends Vue {
  declare $t: (...args: unknown[]) => string;
  loginState: LoginForm = {
    username: '',
    password: '',
  };
  dialogVisible = false;
  dialogVisibleUpdate = false;
  justRegistered = false;
  showThankYou = false;
  justResetPassword = false;
  isLoading = false;
  defaultLogoPathWhite = defaultLogoPathWhite
  userStore = useUserStore();
  router = useRouter();
  route = useRoute();

  created(): void {
    const queryParams = this.route.query;
    if (queryParams.verify === 'check') { this.justRegistered = true; };
    if (queryParams.verify === 'success') { this.showThankYou = true; };
    if (queryParams.verify === 'update-success') this.justResetPassword = true;
    if (verifyKeys.includes((queryParams.verify as string) ?? '')) {
      this.dialogVisible = true;
      this.router.replace('/login');
    }
  }

  async handleUserLogin(): Promise<any> {
    const formData = new FormData();
    formData.append('uid_field', this.loginState.username);
    formData.append('password', this.loginState.password);

    const api = endpointAuthSystem();
    const response = await api.post('api/cekoon/login/authentik', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data
  }

  private async handleLoginSuccess(auth: any): Promise<void> {
    const authentik_session = auth.authentik_session;
    Cookies.set(Key.cookie.access_token_key, authentik_session);
    await initializeSession();
    ElMessage.success(this.$t('success.login'));
    this.redirectToPreviousPage();
  }

  private redirectToPreviousPage(): void {
    const redirectTo = this.route.query.redirect as string | undefined;
    if (redirectTo) {
      try {
        const url = new URL(redirectTo, window.location.origin);
        const path = url.pathname;
        const query = Object.fromEntries(url.searchParams.entries());
        this.router.push({ path, query });
      } catch (e) {
        console.error('Failed to parse redirect URL:', e);
        this.router.push('/');
      }
    } else {
      this.router.push('/');
    }
  }

  private async handleLoginError(error: unknown): Promise<void> {
    const i8error = error as { i8n_name?: string; getTranslatedMessage?: () => string; getTranslatedTitle?: () => string };

    let errorMessage = this.$t('error.register.loginFailed');
    let errorTitle = this.$t('error.login.noRecordFound.title');

    if (i8error?.getTranslatedMessage && typeof i8error.getTranslatedMessage === 'function') {
      errorMessage = i8error.getTranslatedMessage();
    }

    if (i8error?.getTranslatedTitle && typeof i8error.getTranslatedTitle === 'function') {
      errorTitle = i8error.getTranslatedTitle();
    }

    if (i8error?.i8n_name === 'error.login.noRecordFound') {
      await ElMessageBox.confirm(this.$t('error.login.noRecordFound.logout.message'), this.$t('error.login.noRecordFound.logout.title'), {
        confirmButtonText: this.$t('error.login.noRecordFound.logout'),
        cancelButtonText: this.$t('error.login.noRecordFound.cancel'),
        type: 'error',
      });
      window.location.href = import.meta.env.VITE_LOGOUT_URL ?? '';
    } else {
      await ElMessageBox.alert(errorMessage, errorTitle, {
        type: 'error',
        confirmButtonText: 'OK',
      });
    }
  }

  async submitLogin(event: Event | null = null): Promise<void> {
    if (event) event.preventDefault();
    const dataForm = this.$refs.dataForm as InstanceType<typeof ElForm>;
    dataForm?.validate(async (valid: boolean) => {
      if (!valid) {
        ElMessage.error(this.$t('error.register.validation'));
        return;
      }

      this.isLoading = true;
      try {
        const auth = await this.handleUserLogin();
        if (auth.authentik_session) {
          await this.handleLoginSuccess(auth);
        } else {
          ElMessage.error(this.$t('error.register.invalidCredentials'));
        }
      } catch (error: unknown) {
        await this.handleLoginError(error);
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleOK(): void {
    this.justRegistered = false;
    this.dialogVisible = false;
    this.dialogVisibleUpdate = false;
    this.router.push('/login');
  }

  handleGoBack(): void {
    this.router.push('/');
  }
}
</script>

<style scoped>
.dialog {
  font-size: 20px;
  font-weight: 500;
  text-align: center;
}
.dialog-container {
  width: 80%;
}

@media (max-width: 768px) {
  .dialog {
    font-size: 15px;
    font-weight: 500;
  }

  .dialog-container {
    padding: 13%;
    width: 80%;
  }
}

.wrapper {
  /* height:85vh; */
  position: relative;
  overflow-y: auto;
  height: auto;
}

.form-wrapper{
  /* padding-bottom:4rem; */
}

.footer {
  display: flex;
  justify-content: center;
  align-items: center;
  /* height: 71px; */
  background-color: #fff;
  /* background-color: #f0f0f0; */
  padding: 20px 0 0;
}

.button {
  /* margin-top: 20px; */
  width: 100%;
  color: white;
  background-color: var(--color-primary);
  border: none;
}

.button:hover {
  background-color: #353535;
}

#logo {
  max-height: 3.4rem;
}

.custom-input::v-deep(.el-input__wrapper) {
  padding: 1px !important;
}
.custom-input::v-deep(.el-input__inner) {
  border-radius: 4px !important;
  padding: 1px 11px !important;
}
</style>
